// src/components/settings/Settings.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Save,
  AlertCircle,
  CheckCircle,
  User,
  Mail,
  MapPin,
  Globe,
  BookOpen,
  Award,
  Hash,
  Briefcase,
  Lock,
  Eye,
  EyeOff,
  Loader
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from '../utils/axios';
import { showToast } from '../utils/toast';
import CryptoJS from 'crypto-js';

const Settings = () => {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const [formData, setFormData] = useState({
    prefix: '',
    first_name: '',
    last_name: '',
    other_name: '',
    email: '',
    affiliation: '',
    affiliation_country: '',
    affiliation_city: '',
    orcid: '',
    asfi_membership_id: '',
    discipline: '',
    is_available_for_review: ''
  });
  const [originalData, setOriginalData] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});

  // Calculate MD5 hash of email for the endpoint
  const getUserIdHash = (email) => {
    return CryptoJS.MD5(email).toString();
  };

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.email) {
        setFetchLoading(false);
        return;
      }

      setFetchLoading(true);
      try {
        // You might have an endpoint to get user profile data
        // If not, we'll use the user object from auth context
        const userData = {
          prefix: user?.prefix || '',
          first_name: user?.firstName || user?.firstname || '',
          last_name: user?.lastName || user?.lastname || '',
          other_name: user?.otherName || user?.othername || '',
          email: user?.email || '',
          affiliation: user?.affiliations || user?.affiliation || '',
          affiliation_country: user?.affiliationCountry || user?.affiliation_country || '',
          affiliation_city: user?.affiliationCity || user?.affiliation_city || '',
          orcid: user?.orcidId || user?.orcid || '',
          asfi_membership_id: user?.asfiMembershipId || user?.asfi_membership_id || '',
          discipline: user?.discipline || '',
          is_available_for_review: user?.isAvailableForReview || user?.is_available_for_review || 'no'
        };

        setFormData(userData);
        setOriginalData(userData);
      } catch (error) {
        console.error('Error loading user data:', error);
        showToast('Failed to load user data. Please refresh the page.', 'error');
      } finally {
        setFetchLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validatePasswordForm = () => {
    const errors = {};

    if (!passwordData.current_password) {
      errors.current_password = 'Current password is required';
    }

    if (!passwordData.new_password) {
      errors.new_password = 'New password is required';
    } else if (passwordData.new_password.length < 8) {
      errors.new_password = 'Password must be at least 8 characters long';
    }

    if (!passwordData.confirm_password) {
      errors.confirm_password = 'Please confirm your new password';
    } else if (passwordData.new_password !== passwordData.confirm_password) {
      errors.confirm_password = 'Passwords do not match';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if any data has changed
    const hasChanges = Object.keys(formData).some(key => 
      formData[key] !== originalData[key]
    );

    if (!hasChanges && !passwordData.new_password) {
      showToast('No changes were made to your account.', 'info');
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const userIdHash = getUserIdHash(formData.email);
      
      // Prepare update data - only include fields that have changed
      const updatePayload = {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        prefix: formData.prefix,
        other_name: formData.other_name,
        affiliation: formData.affiliation,
        affiliation_country: formData.affiliation_country,
        affiliation_city: formData.affiliation_city,
        orcid: formData.orcid,
        asfi_membership_id: formData.asfi_membership_id,
        discipline: formData.discipline,
        is_available_for_review: formData.is_available_for_review
      };

      // Add password if provided
      if (passwordData.new_password) {
        if (!validatePasswordForm()) {
          setLoading(false);
          return;
        }
        updatePayload.password = passwordData.new_password;
        updatePayload.confirm_password = passwordData.confirm_password;
      }

      const response = await axios.post(
        `/updateAccountData?e=${userIdHash}`,
        updatePayload,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        // Update user context with new data
        updateUser({
          ...user,
          firstName: formData.first_name,
          lastName: formData.last_name,
          prefix: formData.prefix,
          otherName: formData.other_name,
          affiliations: formData.affiliation,
          affiliationCountry: formData.affiliation_country,
          affiliationCity: formData.affiliation_city,
          orcidId: formData.orcid,
          asfiMembershipId: formData.asfi_membership_id,
          discipline: formData.discipline,
          isAvailableForReview: formData.is_available_for_review
        });

        setOriginalData(formData);
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        setShowPasswordForm(false);

        showToast(response.data.success || 'Your account information has been updated successfully.', 'success');

        // If password was changed, log out after 3 seconds
        if (passwordData.new_password) {
          setTimeout(() => {
            logout();
            navigate('/portal/login', { 
              state: { message: 'Password changed successfully. Please log in with your new password.' }
            });
          }, 3000);
        }
      } else if (response.data.error) {
        showToast(response.data.error, 'error');
      }
    } catch (error) {
      console.error('Update error:', error);
      showToast(error.response?.data?.error || error.response?.data?.message || 'Failed to update account information. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const disciplines = [
    'Agricultural sciences',
    'Allied Health Sciences',
    'Anthropology',
    'Arts',
    'Biology',
    'Chemistry',
    'Climate Change',
    'Computer and Information Sciences',
    'Earth Sciences',
    'Engineering and Technology',
    'Environmental Sciences',
    'Epidemiology',
    'Humanities',
    'Leadership and Management',
    'Life Sciences',
    'Materials Science',
    'Mathematics',
    'Medicine',
    'Physics and Astronomy',
    'Politics',
    'Public & Global Health',
    'Social and Behavioral Sciences',
    'Statistics',
    'Theology',
    'Other'
  ];

  if (fetchLoading) {
    return (
      <div className="max-w-4xl mx-auto flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your account information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[--asfi-purple] flex items-center">
          <User className="w-6 h-6 mr-3" />
          Account Settings
        </h1>
        <p className="text-gray-600 mt-1">Manage your account information and preferences</p>
      </div>

      {/* Account Information Form */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center">
            <User className="w-5 h-5 mr-2 text-[--asfi-purple]" />
            Personal Information
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Prefix */}
            <div>
              <label htmlFor="prefix" className="block text-sm font-medium text-gray-700 mb-1">
                Prefix
              </label>
              <select
                id="prefix"
                name="prefix"
                value={formData.prefix}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
              >
                <option value="">Select Preferred Prefix</option>
                <option value="Prof.">Prof.</option>
                <option value="Dr.">Dr.</option>
                <option value="Mr.">Mr.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Miss">Miss</option>
                <option value="Ms.">Ms.</option>
              </select>
            </div>

            {/* First Name */}
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
              />
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
              />
            </div>

            {/* Other Name */}
            <div>
              <label htmlFor="other_name" className="block text-sm font-medium text-gray-700 mb-1">
                Other Name
              </label>
              <input
                type="text"
                id="other_name"
                name="other_name"
                value={formData.other_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
              />
            </div>

            {/* Email */}
            <div className="md:col-span-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                readOnly
                className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
            </div>
          </div>

          <div className="border-t border-gray-200 my-6"></div>

          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Briefcase className="w-5 h-5 mr-2 text-[--asfi-purple]" />
            Affiliation Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Affiliation */}
            <div className="md:col-span-3">
              <label htmlFor="affiliation" className="block text-sm font-medium text-gray-700 mb-1">
                Affiliation/Institution
              </label>
              <input
                type="text"
                id="affiliation"
                name="affiliation"
                value={formData.affiliation}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                placeholder="e.g., University of Lagos"
              />
            </div>

            {/* Country */}
            <div>
              <label htmlFor="affiliation_country" className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <input
                type="text"
                id="affiliation_country"
                name="affiliation_country"
                value={formData.affiliation_country}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                placeholder="e.g., Nigeria"
              />
            </div>

            {/* City */}
            <div>
              <label htmlFor="affiliation_city" className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                id="affiliation_city"
                name="affiliation_city"
                value={formData.affiliation_city}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                placeholder="e.g., Lagos"
              />
            </div>
          </div>

          <div className="border-t border-gray-200 my-6"></div>

          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Award className="w-5 h-5 mr-2 text-[--asfi-purple]" />
            Professional Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* ORCID ID */}
            <div>
              <label htmlFor="orcid" className="block text-sm font-medium text-gray-700 mb-1">
                ORCID ID
              </label>
              <input
                type="text"
                id="orcid"
                name="orcid"
                value={formData.orcid}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                placeholder="0000-0002-3458-8696"
              />
              <p className="mt-1 text-xs text-gray-500">Format: XXXX-XXXX-XXXX-XXXX</p>
            </div>

            {/* ASFI Membership ID */}
            <div>
              <label htmlFor="asfi_membership_id" className="block text-sm font-medium text-gray-700 mb-1">
                ASFI Membership ID
              </label>
              <input
                type="text"
                id="asfi_membership_id"
                name="asfi_membership_id"
                value={formData.asfi_membership_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                placeholder="ASFI-2024-XXXX"
              />
            </div>

            {/* Discipline */}
            <div className="md:col-span-2">
              <label htmlFor="discipline" className="block text-sm font-medium text-gray-700 mb-1">
                Discipline
              </label>
              <select
                id="discipline"
                name="discipline"
                value={formData.discipline}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
              >
                <option value="">Select a Discipline</option>
                {disciplines.map(discipline => (
                  <option key={discipline} value={discipline}>{discipline}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="border-t border-gray-200 my-6"></div>

          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <BookOpen className="w-5 h-5 mr-2 text-[--asfi-purple]" />
            Review Preferences
          </h3>

          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-3">Are you available to review for ASFIRJ? (optional)</p>
            <div className="flex items-center space-x-6">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="is_available_for_review"
                  value="yes"
                  checked={formData.is_available_for_review === 'yes'}
                  onChange={handleChange}
                  className="w-4 h-4 text-[--asfi-purple] focus:ring-[--asfi-purple] mr-2"
                />
                <span className="text-sm text-gray-700">Yes</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="is_available_for_review"
                  value="no"
                  checked={formData.is_available_for_review === 'no'}
                  onChange={handleChange}
                  className="w-4 h-4 text-[--asfi-purple] focus:ring-[--asfi-purple] mr-2"
                />
                <span className="text-sm text-gray-700">No</span>
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Change Password Section */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center">
            <Lock className="w-5 h-5 mr-2 text-[--asfi-purple]" />
            Password
          </h2>
          <button
            type="button"
            onClick={() => {
              setShowPasswordForm(!showPasswordForm);
              setPasswordErrors({});
              setPasswordData({
                current_password: '',
                new_password: '',
                confirm_password: ''
              });
            }}
            className="text-sm text-[--asfi-purple] hover:text-[--asfi-blue] font-medium"
          >
            {showPasswordForm ? 'Cancel' : 'Change Password'}
          </button>
        </div>

        {showPasswordForm && (
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-4 max-w-md">
              {/* Current Password */}
              <div>
                <label htmlFor="current_password" className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    id="current_password"
                    name="current_password"
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    required
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent pr-10 ${
                      passwordErrors.current_password ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordErrors.current_password && (
                  <p className="mt-1 text-xs text-red-500">{passwordErrors.current_password}</p>
                )}
              </div>

              {/* New Password */}
              <div>
                <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    id="new_password"
                    name="new_password"
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    required
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent pr-10 ${
                      passwordErrors.new_password ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordErrors.new_password && (
                  <p className="mt-1 text-xs text-red-500">{passwordErrors.new_password}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">Minimum 8 characters</p>
              </div>

              {/* Confirm New Password */}
              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirm_password"
                    name="confirm_password"
                    value={passwordData.confirm_password}
                    onChange={handlePasswordChange}
                    required
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent pr-10 ${
                      passwordErrors.confirm_password ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordErrors.confirm_password && (
                  <p className="mt-1 text-xs text-red-500">{passwordErrors.confirm_password}</p>
                )}
              </div>

              <p className="text-sm text-yellow-600 bg-yellow-50 p-3 rounded-lg">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Note: You will be logged out after changing your password and will need to log in again.
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Settings;